import type {
  AdminVehicleCatalogMakeDetailDto,
  AdminVehicleCatalogPatchMakeBody,
} from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import CheckIcon from '@mui/icons-material/Check';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AliasSection } from './AliasSection';
import { EntityFields } from './EntityFields';
import { MakeDrawerHeader } from './MakeDrawerHeader';
import { ModelsSection } from './ModelsSection';
import { StaleBanner } from './StaleBanner';
import { patchCatalogMake, useCatalogCache } from '../../../api/admin-vehicle-catalog';
import {
  makeFormSchema,
  makePatchBody,
  makeValues,
  type MakeFormValues,
} from '../_lib/catalogForms';
import { activeModelCount } from '../_lib/modelFilter';
import { useCatalogWrite } from '../_lib/useCatalogWrite';
import { useEntityForm } from '../_lib/useEntityForm';

/**
 * Body of the make drawer (AUT-437), after `vehicle-catalog-make.html`:
 * banners (stale write, not visible in the app), «Марка» (name + switches),
 * «Алиасы марки», «Модели», footer Save.
 *
 * `PATCH /makes/:id` is the only write whose answer replaces the cached
 * detail; everything else is followed by a refetch (the runner awaits it)
 * and Save stays disabled while the detail is `syncing`.
 */
export interface MakeDrawerBodyProps {
  make: AdminVehicleCatalogMakeDetailDto;
  syncing: boolean;
  justCreated: boolean;
  onClose: () => void;
  onOpenMake: (makeId: string) => void;
  onNotFound: () => void;
  onRenamed: () => void;
}

const FLAGS = ['popular', 'onSale', 'hidden'] as const;
const MAKEFORMSCHEMA_RESOLVER = zodResolver(makeFormSchema);
const FORM_ID = 'vehicle-catalog-make-form';

const NotVisibleBanner = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Alert severity="warning">
      <AlertTitle>{t('vehicleCatalog.drawer.notVisibleTitle')}</AlertTitle>
      {t('vehicleCatalog.drawer.notVisibleBody')}
    </Alert>
  );
};

const Footer = ({ canSave, busy }: { canSave: boolean; busy: boolean }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: '14px 24px',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Button
        type="submit"
        form={FORM_ID}
        variant="contained"
        startIcon={<CheckIcon />}
        disabled={!canSave}
        loading={busy}
      >
        {t('vehicleCatalog.drawer.save')}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
        {t('vehicleCatalog.drawer.footerHint')}
      </Typography>
    </Box>
  );
};

export const MakeDrawerBody = (p: MakeDrawerBodyProps): ReactElement => {
  const { t } = useTranslation();
  const cache = useCatalogCache();
  const write = useCatalogWrite(p.onNotFound);
  const entity = useEntityForm<
    MakeFormValues,
    AdminVehicleCatalogPatchMakeBody,
    AdminVehicleCatalogMakeDetailDto
  >({
    resolver: MAKEFORMSCHEMA_RESOLVER,
    values: makeValues(p.make),
    row: p.make,
    syncing: p.syncing,
    write,
    bodyOf: (v) => makePatchBody(v, p.make),
    send: (body) => patchCatalogMake(p.make.id, body),
    apply: cache.putMake,
    successKey: 'vehicleCatalog.drawer.saved',
    onSaved: (body) => {
      if (body.name !== undefined) p.onRenamed();
    },
  });
  const notVisible = p.make.status === 'active' && activeModelCount(p.make.models) === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MakeDrawerHeader make={p.make} notVisible={notVisible} onClose={p.onClose} />
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: '22px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {entity.stale ? <StaleBanner /> : null}
        {notVisible ? <NotVisibleBanner /> : null}
        <form
          id={FORM_ID}
          noValidate
          onSubmit={(e) =>
            void entity.form.handleSubmit(async () => {
              await entity.save();
            })(e)
          }
        >
          <Typography variant="overline" color="text.secondary">
            {t('vehicleCatalog.drawer.section')}
          </Typography>
          <EntityFields
            entity={entity}
            currentName={p.make.name}
            flags={FLAGS}
            disabled={write.busy}
            currentMakeId={p.make.id}
            onOpenMake={p.onOpenMake}
          />
        </form>
        <AliasSection
          title={t('vehicleCatalog.aliases.title')}
          aliases={p.make.aliases}
          makeId={p.make.id}
          write={write}
          disabled={write.busy || p.syncing}
          onOpenMake={p.onOpenMake}
        />
        <ModelsSection
          make={p.make}
          syncing={p.syncing}
          justCreated={p.justCreated}
          write={write}
          onOpenMake={p.onOpenMake}
        />
      </Box>
      <Footer canSave={entity.canSave} busy={write.busy} />
    </Box>
  );
};
